import { buildEditorFromExtensions } from "@lexical/extension";
import { HistoryExtension } from "@lexical/history";
import { $convertFromMarkdownString, $convertToMarkdownString } from "@lexical/markdown";
import { configExtension, defineExtension, type LexicalEditor } from "lexical";
import {
  checkUrl,
  createDocument,
  DEFAULT_URL_POLICY,
  openDocument,
  type DocumentId,
  type EditorDocument,
  type ExportResult,
  UNSPECIFIED_ENGINE_VERSION,
  type UrlPolicy,
} from "../index";
import { createTransformers } from "../features/markdown/transformers";
import { createErrorWatch, parseContent } from "../lexical/parse";
import { PRESET_EXTENSIONS } from "../lexical/presets";
import { AttachmentsExtension } from "../features/attachments/index";
import { $clampListDepth } from "../features/lists/depth";
import { TablesExtension } from "../features/tables/index";

const engineVersion = (editor: LexicalEditor): string =>
  (editor.constructor as typeof LexicalEditor).version ?? UNSPECIFIED_ENGINE_VERSION;

export interface MarkdownOptions {
  /** Which links survive the conversion. Defaults to the editor's own default policy. */
  urlPolicy?: UrlPolicy;
}

export type DocumentToMarkdownResult =
  | ({ ok: true } & ExportResult)
  | { ok: false; reason: "unreadable"; message: string };

// Headless: no root element, no history. Every node type the editor has is registered, those that
// load on demand included, so no stored document is unreadable here that an editor could open.
function headless(onError: (error: Error) => void): ReturnType<typeof buildEditorFromExtensions> {
  return buildEditorFromExtensions(
    defineExtension({
      name: "z-editor/markdown/headless",
      dependencies: [
        PRESET_EXTENSIONS.document,
        TablesExtension,
        AttachmentsExtension,
        configExtension(HistoryExtension, { disabled: true }),
      ],
      onError,
    }),
  );
}

const transformersFor = (options: MarkdownOptions | undefined) => {
  const policy = options?.urlPolicy ?? DEFAULT_URL_POLICY;
  return createTransformers("document", (url) => checkUrl(url, policy).ok);
};

/**
 * Builds a document from Markdown without mounting an editor. The Markdown is read exactly as
 * `apply-edit` with a `document` target reads it, so what an editor would make, this makes.
 */
export function markdownToDocument(
  markdown: string,
  documentId: DocumentId,
  options?: MarkdownOptions,
): EditorDocument {
  const editor = headless((error) => {
    throw error;
  });
  try {
    editor.update(
      () => {
        $convertFromMarkdownString(markdown, transformersFor(options));
        // Markdown can nest a list as deep as it likes; a stored document may not.
        $clampListDepth();
      },
      { discrete: true },
    );
    return createDocument(documentId, editor.getEditorState().toJSON(), engineVersion(editor));
  } finally {
    editor.dispose();
  }
}

/**
 * Writes a stored document as Markdown without mounting an editor: the same text
 * `EditorHandle.exportMarkdown()` gives for it. Content the engine cannot load is reported, never
 * dropped.
 */
export function documentToMarkdown(
  stored: unknown,
  options?: MarkdownOptions,
): DocumentToMarkdownResult {
  const opened = openDocument(stored);
  if (opened.status === "unreadable") {
    return { ok: false, reason: "unreadable", message: opened.issue.message };
  }
  const errors = createErrorWatch(() => {});
  const editor = headless(errors.onError);
  try {
    const parsed = parseContent(editor, opened.document.content, errors.watch);
    if (!parsed.ok) return { ok: false, reason: "unreadable", message: parsed.error.message };
    editor.setEditorState(parsed.state);
    const text = editor
      .getEditorState()
      .read(() => $convertToMarkdownString(transformersFor(options)));
    return { ok: true, text, warnings: [] };
  } finally {
    editor.dispose();
  }
}
